/* =============================================================
   BERTHS — DATA ACCESS
   =============================================================
   The table is identity, place, owner, amenities, size limits
   and booking mode. Prices, blocked time and bookings hang off
   it in their own tables -- see rates.ts, blocks.ts and
   bookings.ts. docs/data-model.md still documents address,
   width, draft, photo and status, which no migration has added.

   THE ONE TRAP: `berths.position` is a generated geography
   column. `select('*')` returns it as a WKB hex blob — long,
   unreadable and useless to the client — and any write that
   includes it fails with:

     column "position" can only be updated to DEFAULT

   So every statement below names its columns, and the insert and
   update payloads carry only the editable ones. Postgres derives
   `position` from latitude/longitude on its own, which is the
   whole point of it being generated.
   ============================================================= */

import { supabase } from './supabase'
import { toDataError } from './errors'
import { AMENITY_ORDER } from './amenities'
import type { Berth, BerthInput } from './types'

/*
 * `company:companies(id, name)` is a PostgREST embed, resolved through the
 * company_id foreign key: one request returns the berth and the name of the
 * company that owns it, which is what every screen here displays. The alias
 * keeps the property singular — the relationship is many-to-one, and
 * `berth.companies.name` would read as though it were a list.
 *
 * It is a read shape only. The insert and update payloads below carry
 * `company_id` and never `company`; sending an embedded object back would be
 * an unknown column.
 *
 * Every query using it ends in `.overrideTypes<…, { merge: false }>()`.
 * supabase-js infers an embed's cardinality from a generated `Database` type,
 * and this panel deliberately has none (see types.ts), so it falls back to
 * assuming many and types `company` as an array. PostgREST actually returns a
 * single object here — the FK is on `berths`, so the relationship is
 * many-to-one — and `Berth` says so. The override tells the compiler what the
 * wire already does.
 */
const COLUMNS =
  'id, name, city, country_code, latitude, longitude, company_id, amenities, max_length_m, max_vessels, booking_mode, response_hours, company:companies(id, name), created_at'

function clean(input: BerthInput): BerthInput {
  return {
    name: input.name.trim(),
    city: input.city.trim(),
    country_code: input.country_code,
    latitude: input.latitude,
    longitude: input.longitude,
    company_id: input.company_id,
    // Sorted into enum declaration order before writing, so two berths with
    // the same amenities hold identical arrays. Postgres does not normalise
    // array order, and `@>` does not care — but a raw row, a diff and an
    // equality check all read better when the order is stable.
    amenities: AMENITY_ORDER.filter((code) => input.amenities.includes(code)),
    // Passed through as-is, null included: null means "not stated", and the
    // column is nullable precisely so that stays distinguishable from a value.
    max_length_m: input.max_length_m,
    max_vessels: input.max_vessels,
    booking_mode: input.booking_mode,
    // Cleared under instant: the database requires response_hours to be set
    // exactly when the mode is manual, so a value left over from switching
    // modes would be rejected rather than ignored.
    response_hours: input.booking_mode === 'manual' ? input.response_hours : null,
  }
}

/**
 * Country, then city, then name — the order the list is read in when you are
 * curating a region at a time.
 *
 * It takes a name resolver rather than sorting on `country_code`, because the
 * codes do not sort the way their names do: CH, DE, FR, LU reads as
 * Switzerland, Germany, France, Luxembourg, which looks like no order at all.
 * The query below still orders by `country_code` so it can use
 * `berths_country_city_idx`; this re-sorts what comes back for display, and is
 * reused after a write so the list does not need re-fetching.
 */
export function berthComparator(
  countryName: (code: string) => string,
): (a: Berth, b: Berth) => number {
  return (a, b) =>
    countryName(a.country_code).localeCompare(countryName(b.country_code)) ||
    a.city.localeCompare(b.city) ||
    a.name.localeCompare(b.name)
}

export async function listBerths(): Promise<Berth[]> {
  const { data, error } = await supabase
    .from('berths')
    .select(COLUMNS)
    .order('country_code', { ascending: true })
    .order('city', { ascending: true })
    .order('name', { ascending: true })
    .overrideTypes<Berth[], { merge: false }>()

  if (error) throw toDataError(error, 'Could not load berths.')
  return data ?? []
}

/**
 * How many rows there are, without fetching any.
 *
 * `head: true` makes it a HEAD request and `count: 'exact'` puts the total in
 * the Content-Range header, so the dashboard costs a couple of response headers
 * rather than every berth row it would immediately throw away. It runs under
 * the same select policy as everything else, so the number is what you may see.
 */
export async function countBerths(): Promise<number> {
  const { count, error } = await supabase
    .from('berths')
    .select('id', { count: 'exact', head: true })

  if (error) throw toDataError(error, 'Could not count berths.')
  return count ?? 0
}

/** One berth, for the detail and edit pages. Null when the id does not exist. */
export async function getBerth(id: number): Promise<Berth | null> {
  const { data, error } = await supabase
    .from('berths')
    .select(COLUMNS)
    .eq('id', id)
    .maybeSingle()
    .overrideTypes<Berth, { merge: false }>()

  if (error) throw toDataError(error, 'Could not load the berth.')
  return data
}

export async function createBerth(input: BerthInput): Promise<Berth> {
  const { data, error } = await supabase
    .from('berths')
    .insert(clean(input))
    .select(COLUMNS)
    .single()
    .overrideTypes<Berth, { merge: false }>()

  if (error) throw toDataError(error, 'Could not create the berth.')
  return data
}

export async function updateBerth(id: number, input: BerthInput): Promise<Berth> {
  const { data, error } = await supabase
    .from('berths')
    .update(clean(input))
    .eq('id', id)
    .select(COLUMNS)
    .single()
    .overrideTypes<Berth, { merge: false }>()

  if (error) throw toDataError(error, 'Could not save the berth.')
  return data
}

export async function deleteBerth(id: number): Promise<void> {
  const { error } = await supabase.from('berths').delete().eq('id', id)
  if (error) throw toDataError(error, 'Could not delete the berth.')
}
